import { useCallback, useEffect, useMemo, useState } from 'react';
import { CircularProgress, Dialog } from '@material-ui/core';
import { PropTypes } from 'prop-types';
import { useSnackbar } from 'notistack';
import trI18n from '@/common/utils/trI18n';
import localStorageUtil from '@/common/utils/localStorageUtil';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import useTranslationJobSubscription from '@/hooks/useTranslationJobSubscription';
import fileTranslationService from '@/service/fileTranslationService';
import { displayTagOfThumbAttach } from '@/component/ticketKnowledge/handler/ListTicketKnowledgeHandler';
import {
    TRANSLATION_SOURCE_LANG_AUTO,
    TRANSLATION_TARGET_STATUS,
    buildOptimisticLangStatusMap,
    buildStatusRows,
    isInlineViewableFileName,
    mergeJobsIntoLangStatusMap,
    mergeLangStatusMaps,
    normalizeS3Key,
    removeJobFromLangStatusMap
} from '@/common/utils/translationJobUtils';
import DataChangePleaseReloadModal from '@/component/common/modal/DataChangePleaseReloadModal';
import OverflowFileName from './OverflowFileName';
import TranslationLanguageSelect from './TranslationLanguageSelect';
import TranslationStatusList from './TranslationStatusList';
import {
    buildRejectedLangNames,
    canStartTranslate,
    dropSourceFromTargets,
    splitRejectedLangs
} from './handler/FileTranslationDialogHandler';
import './FileTranslationDialog.css';
import { buildSnackbarMessage } from '@/component/common/SnackbarMessage';

const ERROR_SNACKBAR_OPTION = { variant: 'error', autoHideDuration: 2000 };

/**
 * ファイル翻訳 dialog for one attachment (TCK-007 / KNW-006).
 *
 * Closing it never cancels anything: translation keeps running on the server and the latest state
 * is loaded again the next time the dialog is opened.
 */
function FileTranslationDialog({ isOpen, onClose, file, detailTicket }) {
    // i18n dictionary re-render, NOT the translation-job subscription below.
    useTranslationSubscription();

    const { enqueueSnackbar } = useSnackbar();

    const [sourceLang, setSourceLang] = useState(TRANSLATION_SOURCE_LANG_AUTO);
    const [targetLangs, setTargetLangs] = useState([]);
    const [langStatusMap, setLangStatusMap] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isStarting, setIsStarting] = useState(false);
    const [isOpenModalReloadPage, setIsOpenModalReloadPage] = useState(false);

    const fileName = file?.sourceFile?.fileName || file?.fileInfo?.fileName || '';
    const fileUrl = file?.sourceFile?.fileUrl || file?.fileInfo?.fileUrl || '';
    const fileSize = file?.sourceFile?.fileSize || file?.fileInfo?.fileSize || '';
    const ticketId = detailTicket?.id || '';

    // Jobs are stored under the organization owning the CONTENT, so reads and the subscription
    // filter on that one. The request DTO still carries the viewer's own org, which is what the
    // Lambda checks permissions against.
    const affiliationOrganization =
        detailTicket?.affiliationOrganization || localStorageUtil.getAffiliationOrganization();
    const sourceS3Url = useMemo(() => normalizeS3Key(fileUrl), [fileUrl]);

    const fileBadge = useMemo(
        () => displayTagOfThumbAttach(detailTicket || {}, fileName),
        [detailTicket, fileName]
    );

    const statusRows = useMemo(() => buildStatusRows(langStatusMap), [langStatusMap]);

    const loadLangStatusMap = useCallback(async () => {
        const { data, errorCode } = await fileTranslationService.getLangStatusMap({
            fileUrl,
            affiliationOrganization
        });

        if (errorCode) {
            enqueueSnackbar(buildSnackbarMessage('COMMSG002'), ERROR_SNACKBAR_OPTION);
            return null;
        }

        return data?.langStatusMap || {};
    }, [fileUrl, affiliationOrganization, enqueueSnackbar]);

    useEffect(() => {
        if (!isOpen) return () => {};

        let isCancelled = false;
        setSourceLang(TRANSLATION_SOURCE_LANG_AUTO);
        setTargetLangs([]);
        setIsLoading(true);

        const load = async () => {
            const map = await loadLangStatusMap();
            if (isCancelled) return;
            setLangStatusMap(map || {});
            setIsLoading(false);
        };

        load();

        return () => {
            isCancelled = true;
        };
    }, [isOpen, loadLangStatusMap]);

    const handleJobUpdated = useCallback(
        job => {
            const incoming = mergeJobsIntoLangStatusMap([job], affiliationOrganization);
            if (!Object.keys(incoming).length) return;
            // Merged, never spread: a retry job that failed must not pull a language back down from
            // the SUCCEEDED entry an older job still holds.
            setLangStatusMap(prev => mergeLangStatusMaps(prev, incoming));
        },
        [affiliationOrganization]
    );

    // The source file was replaced or deleted while the dialog is open: that job no longer counts.
    const handleJobInvalidated = useCallback(job => {
        setLangStatusMap(prev => removeJobFromLangStatusMap(prev, job?.id));
    }, []);

    useTranslationJobSubscription({
        affiliationOrganization,
        sourceS3Url,
        isEnabled: isOpen,
        onJobUpdated: handleJobUpdated,
        onJobInvalidated: handleJobInvalidated
    });

    const handleChangeSourceLang = useCallback(nextSourceLang => {
        setSourceLang(nextSourceLang);
        // The source language silently disappears from the chosen targets - no message by design.
        setTargetLangs(prev => dropSourceFromTargets(prev, nextSourceLang));
    }, []);

    const handleRejectedLangs = useCallback(
        async rejected => {
            if (!(rejected || []).length) return;

            const { unsupported, stale } = splitRejectedLangs(rejected);

            if (unsupported.length) {
                const langNames = buildRejectedLangNames(unsupported, trI18n('LABEL-TRANSLATION-AUTO-DETECT'));
                enqueueSnackbar(
                    buildSnackbarMessage('MSG-TRANSLATION-UNSUPPORTED-LANGS', { '{{langs}}': langNames }),
                    ERROR_SNACKBAR_OPTION
                );
            }

            if (stale.length) {
                const nextMap = await loadLangStatusMap();
                if (nextMap) setLangStatusMap(prev => mergeLangStatusMaps(prev, nextMap));
            }
        },
        [enqueueSnackbar, loadLangStatusMap]
    );

    const handleStartTranslate = useCallback(async () => {
        setIsStarting(true);
        try {
            const { data, errorCode } = await fileTranslationService.startTranslateFile({
                fileUrl,
                fileName,
                fileSize,
                ticketId,
                sourceLang,
                targetLangs
            });

            if (errorCode) {
                // Stale data on the server side: close this dialog and prompt the user to reload.
                // onClose();
                setIsOpenModalReloadPage(true);
                return;
            }

            const { jobId, accepted = [], rejected = [] } = data || {};

            if (accepted.length) {
                setLangStatusMap(prev =>
                    mergeLangStatusMaps(
                        prev,
                        buildOptimisticLangStatusMap({ accepted, sourceFileName: fileName, jobId })
                    )
                );
            }
            setTargetLangs([]);

            await handleRejectedLangs(rejected);
        } finally {
            setIsStarting(false);
        }
    }, [
        fileName,
        fileSize,
        fileUrl,
        handleRejectedLangs,
        onClose,
        sourceLang,
        targetLangs,
        ticketId
    ]);

    const handleOpenTranslatedFile = useCallback(
        async row => {
            if (row?.status !== TRANSLATION_TARGET_STATUS.SUCCEEDED || !row?.jobId) return;

            // A PDF is shown in a new tab. The tab has to be opened synchronously in the click, before
            // any await, or the popup blocker stops it. The Office formats come back as an attachment,
            // so they are downloaded in place and no blank tab is left open.
            const isViewInTab = isInlineViewableFileName(row.fileName);
            const newTab = isViewInTab ? window.open('', '_blank') : null;
            if (newTab) newTab.opener = null;

            const { data, errorCode } = await fileTranslationService.getTranslatedFileUrl({
                jobId: row.jobId,
                lang: row.lang,
                ticketId
            });

            if (errorCode || !data?.url) {
                newTab?.close();
                enqueueSnackbar(buildSnackbarMessage('COMMSG002'), ERROR_SNACKBAR_OPTION);
                return;
            }

            if (newTab) {
                newTab.location.href = data.url;
                return;
            }

            const downloadLink = document.createElement('a');
            downloadLink.href = data.url;
            // Popup blocked for a PDF: never navigate the app itself to the inline file.
            if (isViewInTab) {
                downloadLink.target = '_blank';
                downloadLink.rel = 'noopener noreferrer';
            }
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        },
        [enqueueSnackbar, ticketId]
    );

    return (
        <Dialog open={isOpen} onClose={onClose} className="file-translation-dialog">
            <div className="file-translation-dialog-header">
                <h3>{trI18n('LABEL-FILE-TRANSLATION-DIALOG-TITLE')}</h3>
            </div>

            <div className="file-translation-dialog-body">
                <div className="file-translation-current-file">
                    <div className="file-translation-label">{trI18n('LABEL-FILE-TRANSLATION-CURRENT-FILE')}</div>
                    <div className="file-translation-current-file-info">
                        <div className="file-translation-thumbnail">
                            <img src="/assets/image/others_file.png" alt="" className="file-translation-thumbnail-img" />
                            {fileBadge?.name && (
                                <span
                                    className="file-translation-thumbnail-badge"
                                    style={{ backgroundColor: fileBadge.colorCode }}
                                >
                                    {fileBadge.name}
                                </span>
                            )}
                        </div>
                        <OverflowFileName fileName={fileName} className="file-translation-current-file-name" />
                    </div>
                </div>

                <TranslationLanguageSelect
                    sourceLang={sourceLang}
                    targetLangs={targetLangs}
                    langStatusMap={langStatusMap}
                    onChangeSourceLang={handleChangeSourceLang}
                    onChangeTargetLangs={setTargetLangs}
                />

                {isLoading && (
                    <div className="file-translation-loading">
                        <CircularProgress size={24} />
                    </div>
                )}

                {!isLoading && statusRows.length > 0 && (
                    <TranslationStatusList rows={statusRows} onOpenFile={handleOpenTranslatedFile} />
                )}
            </div>

            <div className="file-translation-dialog-footer">
                <button className="file-translation-btn-cancel" onClick={onClose}>
                    {trI18n('COMBTN006')}
                </button>
                <button
                    className="file-translation-btn-submit"
                    onClick={handleStartTranslate}
                    disabled={!canStartTranslate(targetLangs, isStarting)}
                >
                    {trI18n('LABEL-TRANSLATION-START')}
                </button>
            </div>

            <DataChangePleaseReloadModal
                isOpen={isOpenModalReloadPage}
                setIsOpen={setIsOpenModalReloadPage}
            />
        </Dialog>
    );
}

FileTranslationDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    file: PropTypes.object,
    detailTicket: PropTypes.object
};

export default FileTranslationDialog;
